<template>
  <div class="share" data-testid="share">
    <VModal
      class="text-sm"
      :showing="showing"
      mobile-fullscreen="mobileFullscreen"
      data-testid="share-modal"
      @update:showing="$emit('update:showing', false)"
    >
      <template #header><p class="text-gray-900 text-lg mb-0">Share</p></template>
      <template #default>
        <div
          class="modal-content grid grid-cols-2 gap-y-4 gap-x-24 border border-white overflow-y-auto overflow-x-hidden sm_gap-x-4 sm_gap-y-0 sm_grid-cols-4"
        >
          <div class="share-container justify-end sm_p-8 md_px-12 sm_row-start-2 sm_justify-center">
            <share-network
              class="share-network"
              network="facebook"
              data-testid="share-modal-facebook"
              :url="shareUrl"
              :title="title"
              @open="track('Facebook')"
            >
              <Facebook class="h-8 m-2" />
              <p class="social-title">Facebook</p>
            </share-network>
          </div>
          <div class="share-container justify-start sm_p-8 md_px-12 sm_row-start-2 sm_justify-center">
            <share-network
              class="share-network"
              network="twitter"
              data-testid="share-modal-twitter"
              :url="shareUrl"
              :title="title"
              @open="track('Twitter')"
            >
              <Twitter class="twitter-icon h-8 m-2" />
              <p class="social-title">Twitter</p>
            </share-network>
          </div>
          <div class="share-container justify-end sm_p-8 md_px-12 sm_row-start-2">
            <share-network
              class="share-network"
              network="linkedin"
              data-testid="share-modal-linkedin"
              :url="shareUrl"
              :title="title"
              @open="track('LinkedIn')"
            >
              <LinkedIn class="linkedin-icon h-8 m-2" />
              <p class="social-title">LinkedIn</p>
            </share-network>
          </div>

          <div class="share-container justify-start sm_p-8 md_px-12 sm_row-start-2 sm_justify-center">
            <slot>
              <share-network
                class="share-network"
                network="email"
                data-testid="share-modal-email"
                :url="shareUrl"
                :title="title"
                @open="track('Email')"
              >
                <EnvelopeIcon class="text-gray-900 w-10 h-12" />
                <p class="social-title">Email</p>
              </share-network>
            </slot>
          </div>
        </div>
      </template>
      <template #footer>
        <p class="gray-800 mb-2 font-medium">Link</p>
        <div class="flex flex-wrap justify-center sm_flex-nowrap">
          <ClientOnly>
            <input
              class="text-sm w-full mr-4 rounded-lg border-gray-100 p-3 value outline-none ring-black focus_ring-2"
              type="text"
              readonly
              :value="shareUrl"
              @input="$emit('update:shareUrl', $event)"
            />
          </ClientOnly>
          <VButton
            class="flex items-center flex-shrink-0 mt-6 sm_mt-0"
            size="xs"
            data-testid="share-menu-copy-link-btn"
            :variant="wasUrlCopied ? 'copper' : 'light'"
            @click="copyLink"
          >
            <Square2StackIcon class="w-5 mr-2 inline-block" />
            <span class="font-medium text-sm">
              {{ wasUrlCopied ? 'Copied!' : 'Copy' }}
            </span>
          </VButton>
        </div>
      </template>
    </VModal>
  </div>
</template>

<script lang="ts" setup>
import { EnvelopeIcon, Square2StackIcon } from '@heroicons/vue/24/outline'
import Twitter from '@/components/icons/Twitter.svg'
import Facebook from '@/components/icons/Facebook.svg'
import LinkedIn from '@/components/icons/LinkedIn.svg'

const props = defineProps({
  showing: {
    type: Boolean,
    required: false,
  },
  shareUrl: {
    type: String,
    required: true,
  },
  title: {
    type: String,
    required: true,
  },
  analyticsPrefix: {
    type: String,
    required: true,
  },
  analyticsPayload: {
    type: Object,
    default: () => null,
  },
})

defineEmits(['update:shareUrl', 'update:showing'])

const { $analytics } = useNuxtApp()

const { wasClicked: wasUrlCopied, copyToClipboard } = useCopyToClipboard()

watch(
  () => props.showing,
  newShowing => {
    if (newShowing) {
      $analytics.track('showedScreenShareMenu')
    }
  }
)

function track(link: string): void {
  $analytics.track(`${props.analyticsPrefix}Click${link}Share`, props.analyticsPayload)
}

function copyLink(): void {
  copyToClipboard({ copyText: props.shareUrl })
  $analytics.track(`${props.analyticsPrefix}ClickCopyShareableLink`, props.analyticsPayload)
}
</script>

<style lang="stylus" scoped>
.social-title
  @apply text-gray-700 mb-2 font-normal

.share-container
  @apply flex py-10 pointer-events-auto

.share-network
  @apply flex flex-col items-center

.twitter-icon
  color #1DA1F2

.linkedin-icon
  color #0A66C2

.modal-content
  grid-template-rows: repeat(3, min-content) 1fr
  grid-template-cols: repeat(4, fr)
</style>
